import * as moment from "moment";
import React from "react";

import {
  IFluid,
  ILocalFile
} from "src/types/local-files";

import styled from "../../theme/styled";
import {
  colourHelper,
  fontLineHeightHelper,
  fontSizeHelper,
  fontSpaceHelper,
  fontWeightHelper
} from "../../theme/theme-helper";
import { FullWidthColumnWrapper } from "../confines-wrappers";
import DynamicImage from "../dynamic-image";
import Link from "../link";
import Wysiwyg from "../wysiwyg/wysiwyg";

interface IProps {
  created: string;
  title: string;
  path: string;
  featured: boolean;
  cover: ILocalFile<IFluid>;
  alt: string;
  author: string;
  excerpt: string;
  tags?: ISimpleTag[];
}

export interface ISimpleTag {
  name: string;
  path: {
    alias: string;
  };
}

interface IArticleContainer {
  isFeatured: boolean;
}

const ArticleContainer = styled<IArticleContainer, "div">("div")`
  transition: 0.2s ease-in-out;
  padding-top: ${props => (props.isFeatured ? "8%" : "16%")};
  padding-bottom: ${props => (props.isFeatured ? "8%" : "0%")};
  background-color: ${props =>
    props.isFeatured ?
      ({ theme }) => colourHelper({
        theme,
        variant: "lightestGrey"
      }) :
      "transparent"};

  &:nth-child(2) {
    padding-top: 8%;
  }

  * {
    margin-bottom: 0;
  }

  .article-wrapper {
    .article {
      position: relative;
      margin: 0 auto;
      padding-top: 75%;
      max-width: 56rem;

      .heading {
        position: relative;

        h2 {
          padding-top: 2.4rem;
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "subhead_1"
    })};
          font-weight: ${({ theme }) =>
    fontWeightHelper({
      theme,
      variant: "heavy"
    })};
          line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "default"
    })};
        }

        span {
          display: block;
        }

        .featured,
        .authored-on,
        .authored-by,
        .tags {
          font-weight: ${({ theme }) =>
    fontWeightHelper({
      theme,
      variant: "light"
    })};
        }

        .featured,
        .authored-on {
          position: absolute;
          z-index: 2;
          top: 0;
          left: 0;
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "small_2"
    })};
          line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "normalized"
    })};
          letter-spacing: ${({ theme }) =>
    fontSpaceHelper({
      theme,
      variant: "medium"
    })};
          text-transform: uppercase;
        }

        .authored-on,
        .authored-by {
          color: ${({ theme }) =>
    colourHelper({
      theme,
      variant: "lightGrey"
    })};
        }

        .featured {
          color: ${({ theme }) =>
    colourHelper({
      theme,
      variant: "brandColor"
    })};
        }

        .authored-on {
          text-indent: ${props => (props.isFeatured ? "-99999rem" : "0")};
        }

        .authored-by,
        .tags {
          padding-top: 1.4rem;
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "small_3"
    })};
          line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "small"
    })};
        }
      }

      .image {
        position: absolute;
        z-index: 2;
        top: 0;
        left: 0;
        width: 100%;

        a {
          display: block;
          width: 100%;
        }
      }

      .excerpt {
        padding-top: 2.8rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "body_1"
    })};
        line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "default"
    })};

        * {
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "body_1"
    })};
          line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "default"
    })};
        }
      }

      .tags {
        margin: 0;
        padding-top: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "default"
    })};
        padding-left: 0;
        list-style: none;
        color: ${({ theme }) =>
    colourHelper({
      theme,
      variant: "brandColorLight"
    })};

        li {
          display: inline;

          &:nth-last-child(1n + 2) {
            &:after {
              content: ", ";
            }
          }
        }
      }
    }
  }

  @media (min-width: 480px) {
    .article-wrapper {
      .article {
        .heading {
          h2 {
            font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "subhead_2"
    })};
          }
        }
      }
    }
  }

  @media (min-width: 568px) {
    padding-top: ${props => (props.isFeatured ? "8%" : "8.8rem")};
    padding-bottom: ${props => (props.isFeatured ? "8%" : "0%")};

    &:nth-child(2) {
      padding-top: 4.4rem;
    }
  }

  @media (min-width: 600px) {
    padding-top: ${props => (props.isFeatured ? "4.8rem" : "9.6rem")};
    padding-bottom: ${props => (props.isFeatured ? "4.8rem" : "0%")};

    &:nth-child(2) {
      padding-top: 4.8rem;
    }

    .article-wrapper {
      .article {
        padding-top: 40.2rem;

        .heading {
          h2 {
            font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "title"
    })};
          }

          .featured,
          .authored-on {
            font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "small_3"
    })};
          }

          .authored-by {
            font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "body_1"
    })};
          }
        }

        .excerpt {
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "body_2"
    })};

          * {
            font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "body_2"
    })};
          }
        }
      }
    }
  }

  @media (min-width: 960px) {
    padding-top: ${props => (props.isFeatured ? "6rem" : "6.8rem")};
    padding-bottom: ${props => (props.isFeatured ? "6rem" : "0%")};

    &:nth-child(2) {
      padding-top: 6rem;
    }

    .article-wrapper {
      .article {
        padding-top: 0;
        max-width: none;
        overflow: hidden;

        .image {
          position: static;
          float: left;
          padding-right: 2%;
          width: 50%;
        }

        .heading,
        .excerpt,
        .footer {
          float: right;
          padding-left: 2%;
          width: 50%;
        }

        .heading {
          .featured,
          .authored-on {
            left: 4%;
          }
        }
      }
    }
  }

  @media (min-width: 1024px) {
    .article-wrapper {
      .article {
        .heading {
          margin-top: 2.2rem;
        }
      }
    }
  }
`;

const FullWidthTeaser = ({
  author,
  title,
  featured,
  cover,
  alt,
  excerpt,
  created,
  path,
  tags
}: IProps) => {
  const date = moment.utc(created).format("D MMM YYYY");

  return (
    <ArticleContainer
      className="featured-article-container"
      isFeatured={featured}
    >
      <FullWidthColumnWrapper className="article-wrapper">
        <article className="article">
          <header
            role="banner"
            className="heading"
          >
            <h2>
              <Link
                color="black"
                underlinecolor="brandColor"
                href={path}
              >
                {title}
              </Link>
            </h2>

            {featured && (
              <span className="featured">
                Featured
                <span className="off-screen">
                  {" "}
                  -

                  {" "}
                </span>
              </span>
            )}

            <span className="authored-on">
              <span className="off-screen">
                Published on
                {" "}
              </span>

              {date}

              <span className="off-screen">
                ,
                {" "}
              </span>
            </span>

            <span className="authored-by">
              By
              {" "}

              {author}

              <span className="off-screen">
                .
              </span>
            </span>
          </header>

          <div className="image">
            <Link
              type="none"
              href={path}
            >
              <DynamicImage
                data={cover}
                alt={alt}
              />
            </Link>
          </div>

          {excerpt.charAt(0) === "<" ? (
            <div className="excerpt">
              <Wysiwyg
                type="formatted"
                content={excerpt}
              />
            </div>
          ) : (
            <p className="excerpt">
              {excerpt}
            </p>
          )}

          {tags && (
            <footer
              className="footer"
              role="contentinfo"
            >
              <ul className="tags">
                {tags.map((tag, i: number) => {
                  return (
                    <li key={i}>
                      <Link href={tag.path.alias}>
                        {tag.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </footer>
          )}
        </article>
      </FullWidthColumnWrapper>
    </ArticleContainer>
  );
};

export default FullWidthTeaser;
